import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { money, pct, formatApiError } from "../lib/api";
import PageHeader, { StatusPill } from "../components/app/PageHeader";
import QuoteTimeline from "../components/app/QuoteTimeline";
import ClientCombobox from "../components/app/ClientCombobox";
import QuickAddRow from "../components/app/QuickAddRow";
import {
  Trash2, GitBranch, Copy, Save, Download, Lock, Settings2,
  ChevronDown, ChevronRight, MoreHorizontal, FileText, Sparkles, Plus,
} from "lucide-react";

const CORE_STATUSES = ["draft", "sent", "won", "lost"];
const EXTRA_STATUSES = ["approved", "rejected"];

const emptyItem = (defaults = {}) => ({
  id: crypto.randomUUID(),
  category: defaults.category || "Other",
  item_name: defaults.item_name || "",
  description: defaults.description || "",
  qty: defaults.qty ?? 1,
  unit: defaults.unit || "ea",
  unit_cost: defaults.unit_cost ?? 0,
  unit_sell: defaults.unit_sell ?? 0,
  labour_hours: defaults.labour_hours ?? 0,
  labour_cost_rate: defaults.labour_cost_rate ?? 45,
  labour_sell_rate: defaults.labour_sell_rate ?? 80,
  wastage_pct: defaults.wastage_pct ?? 0,
  manual_override: defaults.manual_override ?? false,
  override_total_sell: defaults.override_total_sell ?? 0,
});

const CATEGORIES = [
  "Vehicle Signage","Building Signage","Window Frosting","Translucent Vinyl",
  "ACM Panels","Banners","Corflutes","Promotional Products",
  "Machine Hire","Design","Installation","Production","Removal","Other",
];

export default function QuoteBuilder() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const nav = useNavigate();

  const [rules, setRules] = useState([]);

  const [quote, setQuote] = useState({
    quote_number: "—",
    client_id: "",
    title: "",
    status: "draft",
    items: [],
    discount_pct: 0,
    notes: "",
    terms: "",
    lost_reason: "",
    parent_quote_id: null,
    is_variation: false,
  });
  const [totals, setTotals] = useState({
    subtotal_cost: 0, subtotal_sell: 0, discount_amount: 0,
    net_sell: 0, gst: 0, total: 0, profit: 0, margin_pct: 0,
    labour_hours_total: 0,
  });
  const [computedItems, setComputedItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [expanded, setExpanded] = useState({});         // per-line expanded details
  const [eventsRefresh, setEventsRefresh] = useState(0);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [showMoreStatuses, setShowMoreStatuses] = useState(false);
  const [showExtras, setShowExtras] = useState(false);  // notes/terms

  useEffect(() => {
    (async () => {
      const ru = await api.get("/pricing-rules");
      setRules(ru.data);
      if (!isNew) {
        try {
          const { data } = await api.get(`/quotes/${id}`);
          setQuote({
            quote_number: data.quote_number, client_id: data.client_id,
            title: data.title, status: data.status,
            items: data.items.map((it) => ({ ...emptyItem(it), ...it })),
            discount_pct: data.discount_pct,
            notes: data.notes || "", terms: data.terms || "",
            lost_reason: data.lost_reason || "",
            parent_quote_id: data.parent_quote_id,
            is_variation: data.is_variation,
          });
        } catch (e) { alert(formatApiError(e)); nav("/quotes"); }
      }
    })();
    // eslint-disable-next-line
  }, [id]);

  const recompute = useCallback(async (items, discount_pct) => {
    try {
      const { data } = await api.post("/quotes/compute", { items, discount_pct });
      setComputedItems(data.items); setTotals(data.totals);
    } catch (e) {}
  }, []);

  useEffect(() => {
    const t = setTimeout(() => recompute(quote.items, quote.discount_pct), 200);
    return () => clearTimeout(t);
  }, [quote.items, quote.discount_pct, recompute]);

  // Autosave
  const autosaveRef = useRef(null);
  useEffect(() => {
    if (isNew) return;
    if (!quote.client_id || !quote.title) return;
    clearTimeout(autosaveRef.current);
    autosaveRef.current = setTimeout(() => doSave(false), 1500);
    return () => clearTimeout(autosaveRef.current);
    // eslint-disable-next-line
  }, [quote]);

  const updateItem = (idx, patch) => {
    setQuote((s) => { const items = s.items.slice(); items[idx] = { ...items[idx], ...patch }; return { ...s, items }; });
  };
  const removeItem = (idx) => setQuote((s) => ({ ...s, items: s.items.filter((_, i) => i !== idx) }));

  const addFromRule = (rule) => {
    const newItem = emptyItem({
      category: rule.category, item_name: rule.item_name,
      description: rule.description || "", unit: rule.unit,
      unit_cost: rule.cost, unit_sell: rule.sell,
      labour_hours: rule.default_labour_hours || 0,
      wastage_pct: rule.default_wastage_pct || 0,
    });
    setQuote((s) => ({ ...s, items: [...s.items, newItem] }));
  };
  const addBlank = (name = "") => {
    const newItem = emptyItem({ item_name: name, category: "Other" });
    setQuote((s) => ({ ...s, items: [...s.items, newItem] }));
    // Auto-expand the new blank line so the user can fill in details
    setTimeout(() => setExpanded((e) => ({ ...e, [newItem.id]: true })), 0);
  };

  const grouped = useMemo(() => {
    const g = {};
    quote.items.forEach((it, idx) => {
      const cat = it.category || "Other";
      (g[cat] = g[cat] || []).push({ item: it, idx, computed: computedItems[idx] });
    });
    return g;
  }, [quote.items, computedItems]);

  const doSave = async (notify = true) => {
    if (!quote.client_id) { if (notify) alert("Pick a client first"); return; }
    if (!quote.title) { if (notify) alert("Give the quote a title"); return; }
    setSaving(true);
    try {
      const payload = {
        client_id: quote.client_id, title: quote.title, status: quote.status,
        items: quote.items, discount_pct: Number(quote.discount_pct) || 0,
        notes: quote.notes || "", terms: quote.terms || "",
        lost_reason: quote.lost_reason || "",
        parent_quote_id: quote.parent_quote_id, is_variation: !!quote.is_variation,
      };
      if (isNew) {
        const { data } = await api.post("/quotes", payload);
        setSavedAt(new Date()); nav(`/quotes/${data.id}`, { replace: true });
      } else {
        await api.put(`/quotes/${id}`, payload);
        setSavedAt(new Date()); setEventsRefresh((n) => n + 1);
      }
    } catch (e) { alert(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const setStatus = async (s) => {
    if (s === "lost" && !quote.lost_reason) {
      const r = prompt("Why we lost it? (price / competitor / scope / timing / other)");
      if (r == null) return;
      setQuote((q) => ({ ...q, status: s, lost_reason: r || "" }));
      if (!isNew) {
        try { await api.post(`/quotes/${id}/status`, { status: s, lost_reason: r || "" }); setSavedAt(new Date()); setEventsRefresh((n) => n + 1); }
        catch (e) { alert(formatApiError(e)); }
      }
      return;
    }
    setQuote((q) => ({ ...q, status: s, ...(s !== "lost" ? { lost_reason: "" } : {}) }));
    if (!isNew) {
      try { await api.post(`/quotes/${id}/status`, { status: s }); setSavedAt(new Date()); setEventsRefresh((n) => n + 1); }
      catch (e) { alert(formatApiError(e)); }
    }
  };

  const onDuplicate = async () => {
    if (isNew) { alert("Save the quote first."); return; }
    const { data } = await api.post(`/quotes/${id}/duplicate`); nav(`/quotes/${data.id}`);
  };
  const onVariation = async () => {
    if (isNew) { alert("Save the quote first."); return; }
    const { data } = await api.post(`/quotes/${id}/variation`); nav(`/quotes/${data.id}`);
  };
  const onDelete = async () => {
    if (isNew) { nav("/quotes"); return; }
    if (!confirm("Delete this quote permanently?")) return;
    await api.delete(`/quotes/${id}`); nav("/quotes");
  };
  const onExportPDF = () => {
    if (isNew) { alert("Save the quote first to make a PDF."); return; }
    api.get(`/quotes/${id}/pdf`, { responseType: "blob" }).then((r) => {
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `${quote.quote_number}.pdf`; a.target = "_blank"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      setEventsRefresh((n) => n + 1);
    }).catch((e) => alert(formatApiError(e)));
  };

  const visibleStatuses = showMoreStatuses ? [...CORE_STATUSES, ...EXTRA_STATUSES] : CORE_STATUSES;
  const hasExtras = quote.notes || quote.terms;

  return (
    <div data-testid="quote-builder-page">
      <PageHeader
        eyebrow={isNew ? "New Quote" : `Quote ${quote.quote_number}`}
        title={quote.title || (isNew ? "Untitled quote" : "Untitled quote")}
        subtitle={
          <span className="flex items-center gap-3 flex-wrap">
            <StatusPill status={quote.status} />
            {quote.is_variation && (
              <span className="status-pill" style={{
                background: "var(--gradient-accent-soft)",
                color: "var(--accent)",
                border: "1px solid var(--border-accent)",
              }}>
                <GitBranch className="w-3 h-3" /> VARIATION
              </span>
            )}
            {savedAt && (
              <span className="text-xs font-mono" style={{ color: "var(--text-faint)" }}>
                Saved {savedAt.toLocaleTimeString()}
              </span>
            )}
            {saving && (
              <span className="text-xs font-mono" style={{ color: "var(--accent)" }}>Saving…</span>
            )}
          </span>
        }
        actions={
          <div className="flex items-center gap-2 relative">
            <button onClick={onExportPDF} disabled={isNew} data-testid="quote-pdf-btn" className="btn btn-secondary">
              <Download className="w-3.5 h-3.5" /> PDF
            </button>
            <button onClick={() => doSave(true)} disabled={saving} data-testid="quote-save-btn" className="btn btn-primary">
              <Save className="w-4 h-4" /> {isNew ? "Create Quote" : "Save"}
            </button>
            <button
              onClick={() => setShowMoreActions((s) => !s)}
              data-testid="quote-more-actions-btn"
              className="btn btn-ghost btn-icon"
              title="More actions"
            >
              <MoreHorizontal className="w-5 h-5" />
            </button>
            {showMoreActions && (
              <div
                className="absolute right-0 top-full mt-2 surface overflow-hidden z-30"
                style={{ background: "var(--surface-2)", minWidth: 200 }}
              >
                <MenuItem icon={Copy} label="Duplicate" disabled={isNew} onClick={() => { setShowMoreActions(false); onDuplicate(); }} testid="quote-duplicate-btn" />
                <MenuItem icon={GitBranch} label="Create variation" disabled={isNew} onClick={() => { setShowMoreActions(false); onVariation(); }} testid="quote-variation-btn" />
                <MenuItem icon={Trash2} label="Delete quote" disabled={isNew} onClick={() => { setShowMoreActions(false); onDelete(); }} testid="quote-delete-btn" danger />
              </div>
            )}
          </div>
        }
      />

      <div className="p-10 grid grid-cols-12 gap-6">
        {/* LEFT */}
        <div className="col-span-8 space-y-5">
          {/* Hero card — Client + Title (the 2 things that matter most) */}
          <div className="surface p-6 space-y-4">
            <div>
              <label className="label-eyebrow block mb-2">For</label>
              <ClientCombobox
                value={quote.client_id}
                onChange={(cid) => setQuote((q) => ({ ...q, client_id: cid }))}
                autoFocus={isNew}
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Job / Quote Title</label>
              <input
                value={quote.title}
                onChange={(e) => setQuote({ ...quote, title: e.target.value })}
                placeholder="e.g. Fleet wrap — 5 vans"
                data-testid="quote-title-input"
                className="input"
                style={{ fontSize: "1.05rem" }}
              />
            </div>
          </div>

          {/* Quick add bar — the fast path. Gets attention treatment when no items yet. */}
          <QuickAddRow
            rules={rules}
            onAddRule={addFromRule}
            onAddBlank={addBlank}
            attentionMode={quote.items.length === 0}
          />

          {/* Items, grouped by category */}
          {Object.keys(grouped).length === 0 ? (
            <div className="surface p-12 text-center" data-testid="empty-items">
              <div
                className="w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-4"
                style={{ background: "var(--gradient-accent-soft)" }}
              >
                <Sparkles className="w-5 h-5" style={{ color: "var(--accent)" }} />
              </div>
              <div className="text-sm" style={{ color: "var(--text-primary)" }}>
                Start adding items above ↑
              </div>
              <div className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>
                Type to search your pricing library, or press <kbd className="font-mono">⌘K</kbd> to focus.
              </div>
            </div>
          ) : (
            Object.entries(grouped).map(([cat, lines]) => (
              <CategoryGroup
                key={cat}
                cat={cat}
                lines={lines}
                expanded={expanded}
                setExpanded={setExpanded}
                onChange={updateItem}
                onRemove={removeItem}
              />
            ))
          )}

          {/* Notes / Terms — collapsed by default unless content already exists */}
          <div className="surface">
            <button
              onClick={() => setShowExtras((s) => !s)}
              className="w-full px-5 py-3 flex items-center justify-between transition-colors"
            >
              <div className="flex items-center gap-2">
                <Settings2 className="w-3.5 h-3.5" style={{ color: "var(--text-muted)" }} />
                <span className="label-eyebrow">Notes & Terms</span>
                {hasExtras && (
                  <span
                    className="status-pill"
                    style={{ background: "var(--gradient-accent-soft)", color: "var(--accent)" }}
                  >
                    Set
                  </span>
                )}
              </div>
              {showExtras
                ? <ChevronDown className="w-4 h-4" style={{ color: "var(--text-faint)" }} />
                : <ChevronRight className="w-4 h-4" style={{ color: "var(--text-faint)" }} />}
            </button>
            {showExtras && (
              <div className="px-5 pb-5 grid grid-cols-2 gap-4 border-t pt-4" style={{ borderColor: "var(--border-subtle)" }}>
                <div>
                  <label className="label-eyebrow block mb-2">Notes (for us)</label>
                  <textarea
                    value={quote.notes || ""}
                    onChange={(e) => setQuote({ ...quote, notes: e.target.value })}
                    rows={4}
                    placeholder="Internal notes — never shown to the client"
                    data-testid="quote-notes-input"
                    className="input"
                  />
                </div>
                <div>
                  <label className="label-eyebrow block mb-2">Terms (on the PDF)</label>
                  <textarea
                    value={quote.terms || ""}
                    onChange={(e) => setQuote({ ...quote, terms: e.target.value })}
                    rows={4}
                    placeholder="Quote valid for 30 days. 50% deposit on acceptance."
                    data-testid="quote-terms-input"
                    className="input"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — Totals + Status + Activity */}
        <div className="col-span-4">
          <div className="sticky top-6 space-y-4">
            <div className="surface overflow-hidden">
              <div
                className="px-5 py-4 border-b"
                style={{
                  borderColor: "var(--border-subtle)",
                  background: "linear-gradient(135deg, rgba(255,61,138,0.10), rgba(168,85,247,0.10))",
                }}
              >
                <div className="eyebrow-accent">Quote Total</div>
                <div
                  className="font-display text-3xl font-bold tracking-tight text-gradient mt-1"
                  data-num data-testid="quote-total"
                >
                  {money(totals.total)}
                </div>
                <div className="text-xs font-mono mt-1" style={{ color: "var(--text-muted)" }}>
                  inc. GST · margin <span
                    data-testid="quote-margin"
                    style={{
                      color: totals.margin_pct >= 30 ? "var(--success)"
                        : totals.margin_pct >= 15 ? "var(--text-primary)"
                        : "var(--accent)",
                      fontWeight: 700,
                    }}
                  >{pct(totals.margin_pct)}</span>
                </div>
              </div>
              <div className="p-5 space-y-2.5 text-sm">
                <Row label="Subtotal" value={money(totals.subtotal_sell)} />
                <div className="flex items-center justify-between">
                  <label className="label-eyebrow">Discount %</label>
                  <input
                    type="number" step="0.5"
                    value={quote.discount_pct}
                    onChange={(e) => setQuote({ ...quote, discount_pct: e.target.value })}
                    data-testid="quote-discount-input"
                    className="input input-mono text-right"
                    style={{ width: 90, padding: "0.35rem 0.55rem" }}
                  />
                </div>
                {totals.discount_amount > 0 && <Row label="− Discount" value={money(totals.discount_amount)} muted />}
                <Row label="GST" value={money(totals.gst)} muted />
                <div className="pt-2 mt-1 border-t flex items-center justify-between" style={{ borderColor: "var(--border-subtle)" }}>
                  <span className="text-xs font-mono" style={{ color: "var(--text-faint)" }}>
                    Cost {money(totals.subtotal_cost)} · {totals.labour_hours_total}h
                  </span>
                  <span className="font-mono text-xs" data-num style={{ color: "var(--text-muted)" }}>
                    Profit {money(totals.profit)}
                  </span>
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="surface p-5">
              <div className="label-eyebrow mb-3">Status</div>
              <div className="grid grid-cols-2 gap-1.5">
                {visibleStatuses.map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatus(s)}
                    data-testid={`status-set-${s}`}
                    className="px-3 py-2 text-[10px] font-bold tracking-widest uppercase rounded transition-all"
                    style={
                      quote.status === s
                        ? (s === "won" || s === "approved")
                          ? { background: "rgba(52,211,153,0.20)", color: "var(--success)", border: "1px solid var(--success)" }
                          : (s === "lost" || s === "rejected")
                          ? { background: "rgba(251,113,133,0.18)", color: "var(--danger)", border: "1px solid var(--danger)" }
                          : { background: "var(--gradient-accent)", color: "white", border: "1px solid transparent" }
                        : { background: "var(--surface-2)", color: "var(--text-muted)", border: "1px solid var(--border-strong)" }
                    }
                  >
                    {s}
                  </button>
                ))}
              </div>
              {!showMoreStatuses && (
                <button
                  onClick={() => setShowMoreStatuses(true)}
                  className="mt-2 text-[10px] font-mono tracking-wider"
                  style={{ color: "var(--text-faint)" }}
                  data-testid="show-more-statuses"
                >
                  Show approved / rejected
                </button>
              )}
              {quote.status === "lost" && (
                <div className="mt-4">
                  <label className="label-eyebrow block mb-1.5" style={{ color: "var(--danger)" }}>Why we lost it</label>
                  <textarea
                    rows={2}
                    value={quote.lost_reason || ""}
                    onChange={(e) => setQuote({ ...quote, lost_reason: e.target.value })}
                    placeholder="competitor 12% cheaper / scope changed…"
                    data-testid="quote-lost-reason-input"
                    className="input"
                    style={{ borderColor: "rgba(251,113,133,0.40)", background: "rgba(251,113,133,0.06)" }}
                  />
                </div>
              )}
            </div>

            {!isNew && <QuoteTimeline quoteId={id} refreshKey={eventsRefresh} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, disabled, testid, danger }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="w-full text-left px-4 py-2.5 flex items-center gap-2.5 text-sm transition-colors"
      style={{
        color: disabled ? "var(--text-faint)" : danger ? "#FECDD3" : "var(--text-secondary)",
        cursor: disabled ? "not-allowed" : "pointer",
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = "var(--surface-3)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

function Row({ label, value, muted }) {
  return (
    <div className="flex items-center justify-between">
      <span className="label-eyebrow" style={muted ? { color: "var(--text-faint)" } : {}}>{label}</span>
      <span className="font-mono text-sm" style={muted ? { color: "var(--text-muted)" } : { color: "var(--text-primary)" }} data-num>{value}</span>
    </div>
  );
}

/* ============================================================
   CategoryGroup — collapsible category with simplified line UX
   ============================================================ */
function CategoryGroup({ cat, lines, expanded, setExpanded, onChange, onRemove }) {
  const groupTotal = lines.reduce((sum, l) => sum + (l.computed?.total_sell || 0), 0);
  return (
    <div className="surface overflow-hidden" data-testid={`category-section-${cat}`}>
      <div className="px-5 py-3 flex items-center justify-between border-b" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="flex items-center gap-3">
          <span className="eyebrow-accent">{cat}</span>
          <span className="text-xs font-mono" style={{ color: "var(--text-faint)" }}>
            {lines.length} item{lines.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="font-mono text-sm font-bold" data-num style={{ color: "var(--text-primary)" }}>
          {money(groupTotal)}
        </div>
      </div>
      <div>
        {lines.map(({ item, idx, computed }) => (
          <LineRow
            key={item.id}
            item={item}
            computed={computed}
            isExpanded={!!expanded[item.id]}
            onToggleExpand={() => setExpanded((e) => ({ ...e, [item.id]: !e[item.id] }))}
            onChange={(patch) => onChange(idx, patch)}
            onRemove={() => onRemove(idx)}
          />
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   LineRow — collapsed by default; expand for advanced controls
   ============================================================ */
function LineRow({ item, computed, isExpanded, onToggleExpand, onChange, onRemove }) {
  const totalSell = computed?.total_sell ?? 0;
  const totalCost = computed?.total_cost ?? 0;

  return (
    <div className="border-b last:border-b-0" style={{ borderColor: "var(--border-subtle)" }} data-testid={`item-row-${item.id}`}>
      {/* Collapsed row — the everyday view */}
      <div className="px-5 py-3 grid grid-cols-12 gap-3 items-center">
        <button
          onClick={onToggleExpand}
          className="col-span-1 flex justify-center"
          data-testid="item-expand-btn"
          title={isExpanded ? "Hide details" : "Show details"}
        >
          {isExpanded
            ? <ChevronDown className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
            : <ChevronRight className="w-4 h-4" style={{ color: "var(--text-faint)" }} />}
        </button>
        <div className="col-span-5 min-w-0">
          <input
            value={item.item_name}
            onChange={(e) => onChange({ item_name: e.target.value })}
            placeholder="Item name"
            data-testid="item-name-input"
            className="w-full bg-transparent outline-none text-sm font-medium"
            style={{ color: "var(--text-primary)" }}
          />
          {item.description && !isExpanded && (
            <div className="text-xs truncate mt-0.5" style={{ color: "var(--text-faint)" }}>
              {item.description}
            </div>
          )}
        </div>
        <div className="col-span-1">
          <input
            type="number" step="0.5" value={item.qty}
            onChange={(e) => onChange({ qty: parseFloat(e.target.value) || 0 })}
            data-testid="item-qty-input"
            className="input input-mono text-right"
            style={{ padding: "0.4rem 0.5rem" }}
            title="Quantity"
          />
        </div>
        <div className="col-span-1 text-center text-xs font-mono" style={{ color: "var(--text-muted)" }}>
          {item.unit}
        </div>
        <div className="col-span-2 flex items-center justify-end gap-1">
          {item.manual_override && <Lock className="w-3 h-3" style={{ color: "var(--accent)" }} />}
          <input
            type="number" step="0.01" value={item.unit_sell}
            onChange={(e) => onChange({ unit_sell: parseFloat(e.target.value) || 0 })}
            data-testid="item-unit-sell-input"
            className="input input-mono text-right"
            style={{ padding: "0.4rem 0.5rem" }}
            title="Sell each"
          />
        </div>
        <div className="col-span-1 text-right">
          <div className="font-mono text-sm font-bold" data-num style={{ color: "var(--text-primary)" }}>
            {money(totalSell)}
          </div>
        </div>
        <div className="col-span-1 flex justify-end">
          <button onClick={onRemove} data-testid="remove-item-btn" className="btn btn-ghost btn-icon" title="Remove line">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Expanded — advanced controls only when needed */}
      {isExpanded && (
        <div
          className="px-5 pb-4 pt-2 grid grid-cols-12 gap-3"
          style={{ background: "var(--surface-inset)" }}
        >
          <div className="col-span-12">
            <label className="label-eyebrow block mb-1.5">Description</label>
            <input
              value={item.description}
              onChange={(e) => onChange({ description: e.target.value })}
              placeholder="Optional description shown on PDF"
              className="input"
              style={{ padding: "0.4rem 0.65rem" }}
            />
          </div>
          <SmallField label="Category" wide>
            <select
              value={item.category}
              onChange={(e) => onChange({ category: e.target.value })}
              className="input input-mono text-xs"
              style={{ padding: "0.4rem 0.5rem" }}
              data-testid="item-category-select"
            >
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </SmallField>
          <SmallField label="Unit">
            <input
              value={item.unit}
              onChange={(e) => onChange({ unit: e.target.value })}
              className="input input-mono"
              style={{ padding: "0.4rem 0.5rem" }}
            />
          </SmallField>
          <SmallField label="Cost ea">
            <input
              type="number" step="0.01" value={item.unit_cost}
              onChange={(e) => onChange({ unit_cost: parseFloat(e.target.value) || 0 })}
              data-testid="item-unit-cost-input"
              className="input input-mono text-right"
              style={{ padding: "0.4rem 0.5rem" }}
            />
          </SmallField>
          <SmallField label="Labour h">
            <input
              type="number" step="0.25" value={item.labour_hours}
              onChange={(e) => onChange({ labour_hours: parseFloat(e.target.value) || 0 })}
              data-testid="item-labour-hours-input"
              className="input input-mono text-right"
              style={{ padding: "0.4rem 0.5rem" }}
            />
          </SmallField>
          <SmallField label="Waste %">
            <input
              type="number" step="1" value={item.wastage_pct}
              onChange={(e) => onChange({ wastage_pct: parseFloat(e.target.value) || 0 })}
              data-testid="item-wastage-input"
              className="input input-mono text-right"
              style={{ padding: "0.4rem 0.5rem" }}
            />
          </SmallField>
          <SmallField label={item.manual_override ? "Override total" : "Total (auto)"} wide>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onChange({ manual_override: !item.manual_override, override_total_sell: totalSell })}
                data-testid="toggle-override"
                className="btn btn-ghost btn-icon"
                style={{ color: item.manual_override ? "var(--accent)" : "var(--text-faint)" }}
                title="Manually override line total"
              >
                <Lock className="w-3.5 h-3.5" />
              </button>
              {item.manual_override ? (
                <input
                  type="number" step="0.01"
                  value={item.override_total_sell}
                  onChange={(e) => onChange({ override_total_sell: parseFloat(e.target.value) || 0 })}
                  data-testid="override-total-sell-input"
                  className="input input-mono text-right"
                  style={{ padding: "0.4rem 0.5rem", borderColor: "var(--accent)" }}
                />
              ) : (
                <div className="font-mono text-sm font-bold flex-1 text-right pr-2" data-num style={{ color: "var(--text-primary)" }}>
                  {money(totalSell)}
                </div>
              )}
            </div>
          </SmallField>
          <div className="col-span-12 text-[11px] font-mono" style={{ color: "var(--text-faint)" }}>
            Computed cost {money(totalCost)} · qty × unit_cost × (1 + waste%) + hours × cost rate
          </div>
        </div>
      )}
    </div>
  );
}

function SmallField({ label, wide, children }) {
  return (
    <div className={wide ? "col-span-3" : "col-span-2"}>
      <label className="label-eyebrow block mb-1">{label}</label>
      {children}
    </div>
  );
}
